/**
 * ai/responseSchema.ts
 *
 * Strict typed contract for all AI responses.
 * AI must return JSON matching this schema exactly.
 * Never accept vague prose — always validate.
 */

// ─── Core AI response (per symbol) ───────────────────────────────────────────

// AIBias semantics — IMPORTANT:
//   bullish / bearish = directional edge exists (even at low conviction)
//   neutral           = no meaningful directional edge (absence of signal, not low volume)
//   mixed             = directional evidence genuinely conflicts (e.g., bullish trend + bearish SMC reversal)
//
// LOW CONVICTION ≠ MIXED.
// When regime + trend + SMC align but RVOL is weak:
//   → use 'bullish' or 'bearish' with conviction/actionability reduced
//   → do NOT collapse to 'mixed' simply because participation is low
export type AIBias = 'bullish' | 'bearish' | 'neutral' | 'mixed';
export type AIMarketState = 'trend' | 'range' | 'transition' | 'high_noise';
export type AIThesisChange = 'new' | 'strengthened' | 'weakened' | 'unchanged' | 'invalidated';
export type AISetupType =
  | 'breakout_long' | 'breakout_short'
  | 'reversion_long' | 'reversion_short'
  | 'continuation_long' | 'continuation_short'
  | 'sweep_reversal_long' | 'sweep_reversal_short'
  | 'wait';
export type AIUrgency = 'low' | 'medium' | 'high';

export interface AIResponse {
  // Identity
  symbol:        string;
  timestamp:     number;       // Unix ms

  // Directional read
  overallBias:   AIBias;
  marketState:   AIMarketState;
  conviction:    number;       // 0–100 — certainty of directional bias; reduce for low participation, NOT direction
  actionability: number;       // 0–100 — setup readiness right now; low RVOL → lower actionability

  // Time horizon for this thesis
  timeHorizon:   'next_15m' | 'next_30m' | 'next_60m';

  // Phase 3: thesis expiry contract — addresses the "two-hour vigil"
  // problem reported by beta testers. AI must commit to when this
  // thesis becomes stale and what specific events would invalidate
  // it before that time. Trader can set a reminder for expiresAt
  // and walk away.
  //
  // expiresAt:                 ISO 8601 string ('2026-04-29T20:00:00Z')
  // invalidationConditions[]:  human-readable predicates ('close above 522.64')
  //
  // This field is OPTIONAL on the wire (existing AI responses don't
  // include it). Validation falls back to a sensible default when
  // missing — see validateAIResponse.
  thesisExpiry?: {
    expiresAt:               string;
    invalidationConditions:  string[];
  };

  // Thesis evolution
  primaryThesis:      string;  // 2-3 sentence core read
  thesisChange:       AIThesisChange;
  whatChanged:        string[]; // specific events since last update
  keyDrivers:         string[]; // top 3 signals driving the read

  // Narrative reads per layer
  liquidityRead:  string;
  regimeRead:     string;
  trapRead:       string;
  smcRead:                string;
  gammaRead:              string;      // "N/A" if no options data
  institutionalFlowRead:  string;      // accumulation/distribution context vs structural bias; "N/A" if no flow data
  confluenceRead:         string;      // deterministic confluence interpretation; references engines + score

  // Actionable plan
  bestSetupType:        AISetupType;
  entryLogic:           string;  // specific, price-level based
  confirmationNeeded:   string;  // what still needs to happen
  invalidation:         string;  // specific price or event that kills the thesis
  targetLogic:          string;
  primaryRisk:          string;
  uncertainty:          string;  // honest disclosure of what AI can't know

  // Decision signal
  shouldTradeNow:  boolean;
  urgency:         AIUrgency;

  // Meta
  modelId:     string;
  inputHash:   string;         // hash of engine inputs — detect if state actually changed
  tokenCount?: number;
}

// ─── Validated response (after schema check) ─────────────────────────────────

export interface ValidatedAIResponse extends AIResponse {
  _valid:    true;
  _validatedAt: number;
}

export interface InvalidAIResponse {
  _valid:  false;
  _error:  string;
  _raw:    string;
}

export type AIResponseResult = ValidatedAIResponse | InvalidAIResponse;

// ─── Required fields for validation ──────────────────────────────────────────

// Phase 3 commit 3.7: 'timestamp' is intentionally NOT in REQUIRED_FIELDS.
// The AI's timestamp value is discarded and we stamp at validation time
// using Date.now(). See the timestamp-stamping section below for details.
const REQUIRED_FIELDS: (keyof AIResponse)[] = [
  'symbol', 'overallBias', 'marketState', 'conviction',
  'actionability', 'timeHorizon', 'primaryThesis', 'thesisChange',
  'whatChanged', 'keyDrivers', 'liquidityRead', 'regimeRead', 'trapRead',
  'smcRead', 'gammaRead', 'institutionalFlowRead', 'confluenceRead',
  'bestSetupType', 'entryLogic', 'confirmationNeeded',
  'invalidation', 'targetLogic', 'primaryRisk', 'uncertainty',
  'shouldTradeNow', 'urgency',
];

const VALID_BIAS:          AIBias[]          = ['bullish','bearish','neutral','mixed'];
const VALID_MARKET_STATE:  AIMarketState[]   = ['trend','range','transition','high_noise'];
const VALID_THESIS_CHANGE: AIThesisChange[]  = ['new','strengthened','weakened','unchanged','invalidated'];
const VALID_SETUP_TYPE:    AISetupType[]     = ['breakout_long','breakout_short','reversion_long','reversion_short','continuation_long','continuation_short','sweep_reversal_long','sweep_reversal_short','wait'];
const VALID_URGENCY:       AIUrgency[]       = ['low','medium','high'];
const VALID_HORIZON                          = ['next_15m','next_30m','next_60m'];

export function validateAIResponse(raw: string, symbol: string): AIResponseResult {
  let parsed: any;
  try {
    // Strip markdown fences if present
    const clean = raw.replace(/^```json\s*/m,'').replace(/^```\s*/m,'').replace(/```\s*$/m,'').trim();
    parsed = JSON.parse(clean);
  } catch (e: any) {
    // Try to extract JSON object from response
    const match = raw.match(/\{[\s\S]*\}/);
    if (match) {
      try { parsed = JSON.parse(match[0]); }
      catch { return { _valid: false, _error: `JSON parse failed: ${e.message}`, _raw: raw }; }
    } else {
      return { _valid: false, _error: 'No JSON object found in response', _raw: raw };
    }
  }

  // Check required fields
  for (const f of REQUIRED_FIELDS) {
    if (parsed[f] === undefined || parsed[f] === null) {
      return { _valid: false, _error: `Missing required field: ${f}`, _raw: raw };
    }
  }

  // Validate enums
  if (!VALID_BIAS.includes(parsed.overallBias))
    return { _valid: false, _error: `Invalid overallBias: ${parsed.overallBias}`, _raw: raw };
  if (!VALID_MARKET_STATE.includes(parsed.marketState))
    return { _valid: false, _error: `Invalid marketState: ${parsed.marketState}`, _raw: raw };
  if (!VALID_THESIS_CHANGE.includes(parsed.thesisChange))
    return { _valid: false, _error: `Invalid thesisChange: ${parsed.thesisChange}`, _raw: raw };
  if (!VALID_SETUP_TYPE.includes(parsed.bestSetupType))
    return { _valid: false, _error: `Invalid bestSetupType: ${parsed.bestSetupType}`, _raw: raw };
  if (!VALID_URGENCY.includes(parsed.urgency))
    return { _valid: false, _error: `Invalid urgency: ${parsed.urgency}`, _raw: raw };
  if (!VALID_HORIZON.includes(parsed.timeHorizon))
    return { _valid: false, _error: `Invalid timeHorizon: ${parsed.timeHorizon}`, _raw: raw };

  // Validate ranges
  if (typeof parsed.conviction !== 'number' || parsed.conviction < 0 || parsed.conviction > 100)
    return { _valid: false, _error: `conviction must be 0-100, got: ${parsed.conviction}`, _raw: raw };
  if (typeof parsed.actionability !== 'number' || parsed.actionability < 0 || parsed.actionability > 100)
    return { _valid: false, _error: `actionability must be 0-100, got: ${parsed.actionability}`, _raw: raw };

  // Validate arrays
  if (!Array.isArray(parsed.whatChanged) || !Array.isArray(parsed.keyDrivers))
    return { _valid: false, _error: 'whatChanged and keyDrivers must be arrays', _raw: raw };

  // Stamp symbol and timestamp.
  //
  // Phase 3 commit 3.7 root-cause fix: the AI was returning hallucinated
  // timestamp values (often dates close to its training cutoff), which were
  // then trusted by the previous logic (`parsed.timestamp || Date.now()`).
  // This caused thesis records to be stamped with year-old dates, producing
  // visible bugs like "EXPIRED 366d ago" in the strip's fallback path and
  // potentially affecting history sort order, audit logs, and any other
  // downstream consumer of `timestamp`.
  //
  // The fix: always stamp at validation time using the server's clock.
  // The AI's `timestamp` field (if any) is discarded. The corresponding
  // prompt-side change removes the field from the JSON schema example so
  // the AI no longer feels obligated to produce one.
  parsed.symbol    = parsed.symbol || symbol;
  parsed.timestamp = Date.now();
  parsed.inputHash = parsed.inputHash || '';

  return { ...parsed, _valid: true, _validatedAt: Date.now() } as ValidatedAIResponse;
}

// ─── Empty/fallback response ──────────────────────────────────────────────────
// Fallback uses 'neutral' (no edge) not 'mixed' (conflicting evidence).
// This distinction matters: 'neutral' = absence of signal, 'mixed' = conflicting signals.

export function fallbackResponse(symbol: string, reason: string): ValidatedAIResponse {
  return {
    symbol, timestamp: Date.now(),
    overallBias: 'neutral', marketState: 'high_noise',
    conviction: 0, actionability: 0,
    timeHorizon: 'next_30m',
    primaryThesis: 'AI analysis unavailable — operating on deterministic signals only.',
    thesisChange: 'unchanged',
    whatChanged: [], keyDrivers: ['AI unavailable'],
    liquidityRead: 'N/A', regimeRead: 'N/A', trapRead: 'N/A',
    smcRead: 'N/A', gammaRead: 'N/A', institutionalFlowRead: 'N/A', confluenceRead: 'N/A',
    bestSetupType: 'wait',
    entryLogic: 'Wait for AI to resume.',
    confirmationNeeded: 'AI reconnection.',
    invalidation: 'N/A', targetLogic: 'N/A',
    primaryRisk: reason,
    uncertainty: 'AI layer is unavailable. All engine signals remain valid.',
    shouldTradeNow: false, urgency: 'low',
    modelId: 'fallback', inputHash: '', _valid: true, _validatedAt: Date.now(),
  };
}
